
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { AdminSearch } from "./shared/AdminSearch";
import { AdminPagination } from "./shared/AdminPagination";
import { CharactersGrid } from "./characters/CharactersGrid";
import { CharacterForm } from "./characters/CharacterForm";
import { CharacterStatusFilter } from "./characters/CharacterStatusFilter";
import { useCharactersManager } from "./characters/useCharactersManager";

export const CharactersManager = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const {
    characters,
    isLoading,
    error,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage,
    totalPages,
    totalCount,
    handleDelete,
    refetch
  } = useCharactersManager();

  const handleEdit = (character: any) => {
    setEditingCharacter(character);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setEditingCharacter(null);
    setIsFormOpen(false);
  };

  const handleFormSuccess = () => {
    handleCloseForm();
    refetch();
  };

  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "published", label: "Published" },
    { value: "draft", label: "Draft" },
    { value: "archived", label: "Archived" },
  ];

  if (error) {
    return (
      <div className="admin-container">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 text-sm">
            Failed to load characters. Please try refreshing the page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">Characters Management</h2>
          <p className="text-gray-600 text-sm mt-1">
            Manage character profiles and information
          </p>
        </div>
        <Button
          onClick={() => setIsFormOpen(true)}
          className="admin-button-primary w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Character
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <AdminSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search characters..."
          />
        </div>
        <CharacterStatusFilter
          selectedValue={statusFilter}
          onFilterChange={setStatusFilter}
          options={statusOptions}
          placeholder="Filter by status"
        />
      </div>

      <CharactersGrid
        characters={characters}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {totalPages > 1 && (
        <AdminPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={12}
          totalItems={totalCount}
          onItemsPerPageChange={() => {}} // Not implemented yet
        />
      )}

      {isFormOpen && (
        <CharacterForm
          character={editingCharacter}
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};
